/**
 * The left navigation — plan §3.4.2. 224px expanded, 60px collapsed, 38px rows,
 * and the collapsed state is remembered per device.
 *
 * On a phone it is not a rail at all. 60px of permanent chrome out of 390px is
 * fifteen per cent of the screen given to a control most people touch twice an
 * hour, so below the frame's phone width the rail leaves the layout and the
 * same rows open as a sheet from the hamburger in the header. Tablets keep the
 * icon rail: there the 60px is affordable and the one-tap reach is worth it.
 *
 * Both draw `NavItems`, so the two cannot drift into different navigations.
 *
 * Items the role cannot see are removed, not greyed (plan §3.4); that decision
 * comes from the same permission matrix the API uses, so the nav and the API
 * can never disagree.
 */
import { useEffect, useRef, useState, type ReactElement } from 'react'
import { createPortal } from 'react-dom'
import type { MeResponse, NavKey } from '@rowted/shared'
import { visibleNavItems } from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { useDismiss } from '../components/overlays.js'
import { Keycap, PlusGlyph } from '../components/keycap.js'
import { MenuRow, NEW_REQUEST_KEYS, NEW_REQUEST_SUB } from '../requests/create-rows.js'
import { IconChevronLeft, IconChevronRight, IconClose, NAV_ICONS } from './icons.js'

/** What the nav's New can create. Only New request, for now (Aaron, A9). */
export type CreateKind = 'request'

export interface NavBadge {
  /** Shown as a pill when expanded, a 7px dot when collapsed. */
  text: string
  /** `accent`: Activity's green pill (`docs/activity.md` §13.2, Board 1). */
  tone?: 'neutral' | 'danger' | 'accent'
}

export interface LeftNavProps {
  me: MeResponse
  active: NavKey | null
  badges?: Partial<Record<NavKey, NavBadge>>
  collapsed: boolean
  onToggleCollapsed: () => void
  onNavigate: (key: NavKey) => void
  /**
   * The nav's **New**, only when given — Rowted Admin and a person without
   * `jobs ≥ edit` see no change (`new-request.md` §13.2).
   */
  onCreate?: (kind: CreateKind) => void
  /** Opened by the **N** key from the office shell (§13.4). */
  createOpen?: boolean
  onCreateOpenChange?: (open: boolean) => void
}

/**
 * The rows themselves, which both the rail and the sheet draw.
 *
 * `touch` raises the row from 38px to 46px and the icon with it: §3.3 puts
 * touch targets at 44px and up, and a sheet somebody opened with their thumb
 * is the clearest case of a touch surface in the office app.
 */
export interface NavRowProps {
  label: string
  icon: (props: { size?: number; className?: string }) => ReactElement | null
  active: boolean
  collapsed: boolean
  touch?: boolean
  badge?: NavBadge
  onClick: () => void
  /** The row's key, as `data-nav-key`, so a test can find one row. */
  navKey?: string
}

/**
 * One row of a rail — the office app's and Rowted Admin's alike.
 *
 * Pulled out of `NavItems` when the admin frame landed
 * (`docs/rowted-admin-v2.md` §4.1: "identical to `LeftNav`"). The two
 * navigations answer to different things — one to `visibleNavItems(permissions)`
 * and a tenant's roles, the other to a fixed list of six — and *that* is where
 * they should differ. The height, the radius, the active tint, the badge and
 * the collapsed dot are the same thing drawn twice, and a copy of them is a
 * copy that goes stale the first time one is adjusted.
 */
export function NavRow({
  label, icon: Icon, active, collapsed, touch, badge, onClick, navKey,
}: NavRowProps) {
  return (
    <button
      type="button"
      data-nav-key={navKey}
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      title={collapsed ? label : undefined}
      className={cn(
        'relative mx-2 flex items-center gap-2.5 px-3',
        touch ? 'h-[46px] text-[14.5px]' : 'h-[38px] text-body',
        'rounded-button',
        collapsed && 'justify-center px-0',
        active
          ? 'bg-accent-soft font-semibold text-accent'
          : 'font-medium text-text hover:bg-hover',
      )}
    >
      <Icon size={touch ? 20 : 18} />
      {!collapsed && <span className="truncate">{label}</span>}

      {badge && !collapsed && (
        <span
          className={cn(
            'ml-auto rounded-chip py-px text-tag',
            badge.tone === 'accent'
              /* Board 1: 10.5/700, 7px sides, white on the accent. */
              ? 'bg-accent px-[7px] font-bold text-white'
              : badge.tone === 'danger'
                ? 'bg-danger-bg px-1.5 font-semibold text-danger'
                : 'bg-line-soft px-1.5 font-semibold text-text-muted',
          )}
        >
          {badge.text}
        </span>
      )}

      {badge && collapsed && (
        <span
          aria-label={badge.text}
          className={cn(
            'absolute right-2 top-2 h-[7px] w-[7px] rounded-full',
            badge.tone === 'danger' ? 'bg-danger' : 'bg-accent',
          )}
        />
      )}
    </button>
  )
}


/**
 * The nav's **New** — `docs/new-request.md` §13.2, Board 1 A, D–F.
 *
 * **No fill and no outline** (Aaron, review 3: a tinted row "looks like the
 * selected menu item, so it conflicts with the one that is actually
 * selected"): a 38px row holding a 24px solid accent tile with a white +,
 * *New* in the nav's own ink, and the key **N** where a page shows a count.
 * Hover and open take the rows' neutral hover fill, never `accent-soft`,
 * which means *the page you are on*. Collapsed it is the tile alone; in the
 * phone sheet it is 46px with a 26px tile and no key.
 *
 * It opens **Create**: a heading and one row, *New request*, which the **R**
 * key also chooses while it is open.
 */
export function NavNewButton({
  collapsed, touch, open, onOpenChange, onCreate,
}: {
  collapsed: boolean
  touch?: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreate: (kind: CreateKind) => void
}) {
  const button = useRef<HTMLButtonElement>(null)
  const menu = useRef<HTMLDivElement>(null)
  const [at, setAt] = useState<{ left: number; top: number } | null>(null)
  useDismiss(open && !touch, () => onOpenChange(false), menu)

  /*
   * Beside the button, 16px to its right — 8px past the nav's border — and
   * 2px above its top (desktop, Board 1 D).
   */
  useEffect(() => {
    if (!open || touch) return
    const rect = button.current?.getBoundingClientRect()
    if (rect) setAt({ left: rect.right + 16, top: rect.top - 2 })
  }, [open, touch, collapsed])

  /* R chooses New request while the menu is open (§13.4); Esc closes it. */
  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return
      if (event.key === 'r' || event.key === 'R') {
        event.preventDefault()
        onOpenChange(false)
        onCreate('request')
      } else if (event.key === 'Escape') {
        onOpenChange(false)
        button.current?.focus()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onOpenChange, onCreate])

  const choose = () => { onOpenChange(false); onCreate('request') }

  const rows = (
    <div role="menu" aria-label="Create" className="flex flex-col">
      <span className="px-3.5 pb-1 pt-2 text-label uppercase text-text-faint">Create</span>
      <MenuRow tile feature title="New request" sub={NEW_REQUEST_SUB}
        keys={touch ? undefined : NEW_REQUEST_KEYS} onClick={choose} />
    </div>
  )

  return (
    <div className="flex flex-col">
      <button
        ref={button}
        type="button"
        data-nav-new=""
        aria-haspopup="menu"
        aria-expanded={open}
        title={collapsed ? 'New · N' : undefined}
        onClick={() => onOpenChange(!open)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown') { event.preventDefault(); onOpenChange(true) }
        }}
        className={cn(
          'mx-2 flex items-center rounded-button text-text',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-ring',
          touch ? 'h-[46px] gap-2.5 px-3 text-[14.5px]' : 'h-[38px] gap-2.5 pl-[9px] pr-2.5 text-body',
          collapsed && 'justify-center px-0',
          'font-semibold hover:bg-hover',
          open && 'bg-hover',
        )}
      >
        <span className={cn('inline-flex shrink-0 items-center justify-center rounded-tile-small',
          'bg-accent text-white', touch ? 'h-[26px] w-[26px]' : 'h-6 w-6')}>
          <PlusGlyph size={14} />
        </span>
        {!collapsed && <span>New</span>}
        {!collapsed && !touch && <Keycap className="ml-auto">N</Keycap>}
      </button>

      {/* In the phone sheet the menu opens under the button. */}
      {open && touch && <div className="mx-2 mt-1">{rows}</div>}

      {open && !touch && at && createPortal(
        <div
          ref={menu}
          style={{ left: at.left, top: at.top }}
          className={cn('fixed z-50 w-[320px] rounded-card border border-line bg-surface pb-1.5 pt-1',
            'shadow-popover')}
        >
          {rows}
        </div>,
        document.body,
      )}
    </div>
  )
}

/** The rule between New and the rest: 10px above, 8px below. */
function NavRule() {
  return <div className="mx-3 mb-2 mt-2.5 h-px bg-line-faint" />
}

function NavItems({
  me, active, badges, collapsed, touch, onNavigate,
}: {
  me: MeResponse
  active: NavKey | null
  badges: Partial<Record<NavKey, NavBadge>>
  collapsed: boolean
  touch?: boolean
  onNavigate: (key: NavKey) => void
}) {
  return (
    <>
      {visibleNavItems(me.permissions, { officeApp: true }).map((item) => (
        <NavRow
          key={item.key}
          navKey={item.key}
          label={item.label}
          icon={NAV_ICONS[item.key]}
          active={item.key === active}
          collapsed={collapsed}
          touch={touch}
          badge={badges[item.key]}
          onClick={() => onNavigate(item.key)}
        />
      ))}
    </>
  )
}

export function LeftNav({
  me, active, badges = {}, collapsed, onToggleCollapsed, onNavigate, onCreate, createOpen = false,
  onCreateOpenChange,
}: LeftNavProps) {
  return (
    <nav
      aria-label="Main"
      className={cn(
        'flex shrink-0 flex-col gap-0.5 border-r border-line bg-surface pt-2.5 transition-[width]',
        collapsed ? 'w-nav-collapsed' : 'w-nav',
      )}
    >
      {onCreate && (
        /* `contents`: one handle on the tile and its rule that adds no box of its own. */
        <div data-nav-create="" className="contents">
          <NavNewButton
            collapsed={collapsed}
            open={createOpen}
            onOpenChange={(open) => onCreateOpenChange?.(open)}
            onCreate={onCreate}
          />
          <NavRule />
        </div>
      )}
      <NavItems
        me={me}
        active={active}
        badges={badges}
        collapsed={collapsed}
        onNavigate={onNavigate}
      />

      <button
        type="button"
        onClick={onToggleCollapsed}
        className={cn(
          'mx-2 mt-auto mb-2 flex h-[38px] items-center gap-2.5 rounded-button px-3',
          'text-body font-medium text-text-muted hover:bg-hover',
          collapsed && 'justify-center px-0',
        )}
      >
        {collapsed ? <IconChevronRight size={18} /> : <IconChevronLeft size={18} />}
        {!collapsed && <span>Collapse</span>}
      </button>
    </nav>
  )
}

export interface NavSheetProps extends Omit<LeftNavProps,
  'collapsed' | 'onToggleCollapsed' | 'createOpen' | 'onCreateOpenChange'> {
  open: boolean
  onClose: () => void
}

/**
 * The phone navigation: the same rows, over the page, from the left.
 *
 * From the left because that is where the rail it replaces was, and because a
 * hamburger at the top-left opening a panel from anywhere else is a small lie
 * about where you are going.
 *
 * It closes on Escape, on the scrim, and on choosing something — the last one
 * matters most: a sheet that stays open behind the page it just navigated to
 * is a sheet somebody has to dismiss before they can read what they asked for.
 */
export function NavSheet({
  me, active, badges = {}, open, onClose, onNavigate, onCreate,
}: NavSheetProps) {
  const panel = useRef<HTMLDivElement>(null)
  const [creating, setCreating] = useState(false)
  useDismiss(open, onClose, panel)
  useEffect(() => { if (!open) setCreating(false) }, [open])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex bg-black/25">
      {/*
        * The scrim is a real element over the whole screen, which is what
        * stops a thumb that misses the sheet scrolling the list behind it —
        * the touch lands here rather than on the page.
        */}
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
        className="flex h-full w-[272px] max-w-[82vw] flex-col bg-surface shadow-slideover"
      >
        <header className="flex h-header shrink-0 items-center justify-between border-b border-line pl-4 pr-2">
          <span className="text-card text-text">{me.tenant.name}</span>
          <button
            type="button"
            aria-label="Close menu"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-field text-text-muted hover:bg-hover"
          >
            <IconClose size={18} />
          </button>
        </header>

        <nav
          aria-label="Main"
          className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto py-2 pb-safe"
        >
          {onCreate && (
            <div data-nav-create="" className="contents">
              <NavNewButton
                collapsed={false}
                touch
                open={creating}
                onOpenChange={setCreating}
                onCreate={(kind) => { onClose(); onCreate(kind) }}
              />
              <NavRule />
            </div>
          )}
          <NavItems
            me={me}
            active={active}
            badges={badges}
            collapsed={false}
            touch
            onNavigate={(key) => { onNavigate(key); onClose() }}
          />
        </nav>
      </div>
    </div>
  )
}
